import {
  COLOR_FAMILIES,
  type ColorFamily,
  MAX_UNIT_PRICE,
  PUBLIC_MAX_MIN_ORDER_QTY,
  PUBLIC_PRODUCTS_MAX_PAGE,
  type PublicProductSort,
} from '@repo/api-contracts';
import type { StoreProductsParams } from '@/entities/product';
import { STORE_EXHIBITION_PARAM } from '@/shared/config/paths';

/**
 * /store 의 필터·정렬 상태는 URL 쿼리가 단일 원천이다 —
 * 링크 공유·뒤로가기가 상태를 그대로 복원한다.
 */

const SORT_TO_PARAM: Readonly<Record<PublicProductSort, string>> = {
  RECOMMENDED: 'recommended',
  PRICE_ASC: 'price-asc',
  PRICE_DESC: 'price-desc',
};

const PARAM_TO_SORT: Readonly<Record<string, PublicProductSort>> = {
  recommended: 'RECOMMENDED',
  'price-asc': 'PRICE_ASC',
  'price-desc': 'PRICE_DESC',
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isColorFamily = (value: string): value is ColorFamily =>
  (COLOR_FAMILIES as readonly string[]).includes(value);

/** 계약 상한과 동일하게 자른다 — 범위 밖 값이 서버로 가서 400 을 받는 대신 여기서 버린다. */
const parseIntInRange = (
  raw: string | null,
  minimum: number,
  maximum: number,
): number | undefined => {
  if (raw === null || raw === '') {
    return undefined;
  }

  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed >= minimum && parsed <= maximum ? parsed : undefined;
};

/** 잘못된 값은 조용히 버리고 기본값으로 돌아간다 — 공유된 링크가 깨지지 않게. */
export const parseStoreSearchParams = (searchParams: URLSearchParams): StoreProductsParams => {
  const rawCategory = searchParams.get('category');
  const rawColors = searchParams.get('colors');
  const rawExhibition = searchParams.get(STORE_EXHIBITION_PARAM);
  const colorFamilies =
    rawColors === null ? undefined : [...new Set(rawColors.split(',').filter(isColorFamily))];

  const priceMin = parseIntInRange(searchParams.get('priceMin'), 0, MAX_UNIT_PRICE);
  const priceMax = parseIntInRange(searchParams.get('priceMax'), 0, MAX_UNIT_PRICE);
  // 역전된 범위(min > max)는 의도를 살려 교환한다 — 그대로 두면 항상 빈 목록이 된다.
  const isInverted = priceMin !== undefined && priceMax !== undefined && priceMin > priceMax;

  return {
    sort: PARAM_TO_SORT[searchParams.get('sort') ?? ''] ?? 'RECOMMENDED',
    page: parseIntInRange(searchParams.get('page'), 1, PUBLIC_PRODUCTS_MAX_PAGE) ?? 1,
    categoryId: rawCategory !== null && UUID_PATTERN.test(rawCategory) ? rawCategory : undefined,
    exhibitionId:
      rawExhibition !== null && UUID_PATTERN.test(rawExhibition) ? rawExhibition : undefined,
    minOrderQtyMax: parseIntInRange(searchParams.get('maxQty'), 1, PUBLIC_MAX_MIN_ORDER_QTY),
    unitPriceMin: isInverted ? priceMax : priceMin,
    unitPriceMax: isInverted ? priceMin : priceMax,
    colorFamilies:
      colorFamilies !== undefined && colorFamilies.length > 0 ? colorFamilies : undefined,
  };
};

/** 기본값(추천순·필터 없음)은 URL 에서 생략한다 — 깨끗한 기본 주소 유지. */
export const toStoreSearchParams = (filters: StoreProductsParams): URLSearchParams => {
  const params = new URLSearchParams();

  if (filters.sort !== 'RECOMMENDED') {
    params.set('sort', SORT_TO_PARAM[filters.sort]);
  }
  if (filters.page > 1) {
    params.set('page', String(filters.page));
  }
  if (filters.categoryId !== undefined) {
    params.set('category', filters.categoryId);
  }
  if (filters.exhibitionId !== undefined) {
    params.set(STORE_EXHIBITION_PARAM, filters.exhibitionId);
  }
  if (filters.minOrderQtyMax !== undefined) {
    params.set('maxQty', String(filters.minOrderQtyMax));
  }
  if (filters.unitPriceMin !== undefined) {
    params.set('priceMin', String(filters.unitPriceMin));
  }
  if (filters.unitPriceMax !== undefined) {
    params.set('priceMax', String(filters.unitPriceMax));
  }
  if (filters.colorFamilies !== undefined && filters.colorFamilies.length > 0) {
    params.set('colors', filters.colorFamilies.join(','));
  }

  return params;
};
